import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import { usePipe } from '@render/composables/usePipe'
import { tiptapToText } from '@render/lib/tiptapToText'

// Global translation key for PrimerCheckout's built-in submit ("Pay") button.
// Primer renders that button inside its own SDK and localizes the label from
// its `locale` only — it exposes no API to set the text — so when this
// translation is authored we override the button with it (see PrimerCheckout.vue).
// When it is NOT authored we deliberately leave Primer's own localized label in
// place rather than forcing an English default (Primer already localizes it per
// language). Authors can also override per-component via the "Submit Button
// Label" field, which wins over this global key.
//
// Primer-only by design: this lives next to the Primer component (rather than in
// the shared `useCheckoutMessages`, which backs the loading/error messages of
// BOTH checkouts) because YunoCheckout renders its own submit button through the
// Yuno SDK and must not pick this label up.
export const PRIMER_SUBMIT_BUTTON_TRANSLATION_KEY = 'global.primerSubmitButton'

// Plain-text label for Primer's submit button, resolved by priority:
//   1. `customKey` — the author's per-component "Submit Button Label" field (an
//      optional pipe-scoped translation key), when it resolves to text;
//   2. the global `primerSubmitButton` translation, when authored for the
//      active language;
//   3. `undefined` — neither is set, so the caller leaves Primer's own
//      SDK-localized label in place (we never force an English default).
// Unlike the loading/error messages (rich TipTap docs rendered inline), the
// submit button is a native Primer element that only accepts a plain string, so
// we flatten the translation doc to text via the shared `tiptapToText` helper.
export function usePrimerSubmitButtonLabel(
  customKey?: MaybeRefOrGetter<string | undefined>,
): ComputedRef<string | undefined> {
  const pipe = usePipe()
  return computed(() => {
    const custom = tiptapToText(pipe.getTranslation(toValue(customKey)))
    if (custom) return custom
    const global = tiptapToText(pipe.getTranslation(PRIMER_SUBMIT_BUTTON_TRANSLATION_KEY))
    return global || undefined
  })
}
